import React, { useState, useEffect, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import ClientAppShell from "../../components/ClientAppShell";
import { Select } from "../../components/ui";
import SmeAssessTab from "../../components/sme/SmeAssessTab";
import Cap from "../components/cap";
import Chat from "../components/smechat";
import { getProjectUserList } from "../../services/pofile";
import { getProjectState } from "../../services/dashboard";
import { useAuth } from "../../auth/AuthContext";
import "../../assets/css/dashboard.css";
import "../../assets/css/capablities.css";
import "../../assets/css/assessment-theme.css";
import "../../assets/css/chat-theme.css";
import "../../assets/css/sme-assessment-tracking.css";
import "../../assets/css/su-pages-theme.css";
import "../../styles/sme-workspace.css";

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Not started":
      return "app-status app-status--danger";
    case "In progress":
      return "app-status app-status--warning";
    case "Completed":
      return "app-status app-status--success";
    case "Error":
      return "app-status app-status--danger";
    default:
      return "app-status app-status--muted";
  }
};

const CapAsses = () => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [serverError, setServerError] = useState("");
  const [projectId, setProjectId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedTab, setSelectedTab] = useState("capability");
  const [teamMemberStatuses, setTeamMemberStatuses] = useState({});

  const teamMemberOptions = useMemo(
    () =>
      teamMembers.map((member) => ({
        value: String(member.userId),
        label: member.userName,
      })),
    [teamMembers]
  );

  const assessUser = useMemo(() => {
    if (!selectedTeamMember || !projectId) {
      return null;
    }
    return {
      project: { projectId: parseInt(projectId, 10) },
      userId: selectedTeamMember.userId,
    };
  }, [selectedTeamMember, projectId]);

  const parsedProjectId = projectId ? parseInt(projectId, 10) : null;

  const handleMenuSelection = (item) => {
    setSelectedMenuItem(item);
  };

  useEffect(() => {
    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(storedProjectId);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setCurrentUserId(user.userId);
    }
  }, [user]);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (projectId && currentUserId) {
        try {
          const payload = {
            UserId: currentUserId,
            ProjectId: parseInt(projectId, 10),
          };
          const response = await getProjectUserList(payload);
          if (response.data) {
            setTeamMembers(response.data);
            fetchTeamMemberStatuses(response.data);
          } else {
            setServerError("No team members found.");
          }
        } catch (error) {
          setServerError("Error fetching team members. " + error.message);
        }
      }
    };
    fetchTeamMembers();
  }, [projectId, currentUserId]);

  const fetchTeamMemberStatuses = async (members) => {
    if (!members || members.length === 0) return;

    try {
      const statuses = {};

      for (const member of members) {
        const payload = {
          userId: member.userId,
          projectId: [parseInt(projectId, 10)],
        };

        try {
          const statusResponse = await getProjectState(payload);
          if (statusResponse && statusResponse.data && statusResponse.data.length > 0) {
            const state = statusResponse.data[0].state;

            let status = "Unknown";
            if (state === 0) status = "Not started";
            else if (state >= 1 && state <= 2) status = "In progress";
            else status = "Completed";

            statuses[member.userId] = status;
          } else {
            statuses[member.userId] = "Unknown";
          }
        } catch (error) {
          console.error(`Error fetching status for user ${member.userId}:`, error);
          statuses[member.userId] = "Error";
        }
      }

      setTeamMemberStatuses(statuses);
    } catch (error) {
      setServerError("Error fetching team member statuses: " + error.message);
    }
  };

  const handleTeamMemberChange = (userId) => {
    setSelectedMemberId(userId);
    const member = teamMembers.find(
      (member) => member.userId.toString() === userId
    );
    setSelectedTeamMember(member || null);
  };

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
  };

  const renderEmptySelection = (message) => (
    <div className="su-empty-selection">
      <p className="su-empty-selection__title">No team member selected</p>
      <p>{message}</p>
    </div>
  );

  const memberStatus = selectedTeamMember
    ? teamMemberStatuses[selectedTeamMember.userId] || "Unknown"
    : null;

  return (
    <ClientAppShell
      title="Capabilities & Assessment Tracking"
      dataNav="cap-assess"
      selectedItem={selectedMenuItem}
      onMenuSelect={handleMenuSelection}
      containerClassName="su-cap-assess-scroll sme-tracking-page"
      intro={{
        eyebrow: 'Team oversight',
        description:
          'Review each member’s capability selections, assessment progress, and chat activity—pick a teammate to open their workspace.',
      }}
    >
      {teamMembers.length > 0 && (
        <div className="su-member-picker">
          <Select
            id="teamMemberDropdown"
            label="Select Team Member"
            placeholder="Choose a team member"
            value={selectedMemberId}
            onChange={handleTeamMemberChange}
            options={teamMemberOptions}
            className="su-member-picker__field"
            aria-label="Select team member"
          />

          {selectedTeamMember && memberStatus && (
            <span className={getStatusBadgeClass(memberStatus)}>
              Status: {memberStatus}
            </span>
          )}
        </div>
      )}

      <div className="dash-frame-wrapper">
        <div className="dash-frame-div su-cap-assess-page">
          <div className="su-tab-workspace">
            <div
              className="sme-tracking-tabs"
              role="tablist"
              aria-label="Capability and assessment sections"
            >
              <button
                type="button"
                role="tab"
                aria-selected={selectedTab === "capability"}
                className={`sme-tracking-tab ${selectedTab === "capability" ? "sme-tracking-tab--active" : ""}`}
                onClick={() => handleTabClick("capability")}
              >
                Capability
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={selectedTab === "assessment"}
                className={`sme-tracking-tab ${selectedTab === "assessment" ? "sme-tracking-tab--active" : ""}`}
                onClick={() => handleTabClick("assessment")}
              >
                Assessment
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={selectedTab === "chat"}
                className={`sme-tracking-tab ${selectedTab === "chat" ? "sme-tracking-tab--active" : ""}`}
                onClick={() => handleTabClick("chat")}
              >
                Chat
              </button>
            </div>

            <div className="sme-tracking-tab-panels">
              <div
                id="capability-tab"
                role="tabpanel"
                className="su-tab-panel su-cap-panel"
                hidden={selectedTab !== "capability"}
              >
                {selectedTeamMember ? (
                  <Cap user={selectedTeamMember} />
                ) : (
                  renderEmptySelection(
                    "Please select a team member to view capability details."
                  )
                )}
              </div>

              <div
                id="assessment-tab"
                role="tabpanel"
                className="su-tab-panel su-assess-panel sme-tracking-tab-panel sme-assessment-wrapper"
                hidden={selectedTab !== "assessment"}
              >
                {selectedTeamMember && parsedProjectId ? (
                  <div className="sme-assessment-container">
                    <div className="sme-workspace su-assess-sme-view">
                      <SmeAssessTab
                        assessUser={assessUser}
                        projectId={parsedProjectId}
                        clientUserId={selectedTeamMember.userId}
                        isTabActive={selectedTab === "assessment"}
                      />
                    </div>
                  </div>
                ) : (
                  renderEmptySelection(
                    "Please select a team member to view assessment details."
                  )
                )}
              </div>

              <div
                id="chat-tab"
                role="tabpanel"
                className="su-tab-panel su-tab-panel--chat su-chat-panel sme-tracking-chat-panel"
                hidden={selectedTab !== "chat"}
              >
                {selectedTab === "chat" && selectedTeamMember && projectId ? (
                  <Chat
                    key={`chat-${projectId}-${selectedTeamMember.userId}`}
                    project={{ projectId: parseInt(projectId, 10) }}
                    clientUserId={selectedTeamMember.userId}
                    readOnly
                  />
                ) : selectedTab === "chat" ? (
                  renderEmptySelection(
                    "Please select a team member to view chat details."
                  )
                ) : null}
              </div>
            </div>

            {serverError && (
              <div className="sme-tracking-alert sme-tracking-alert--error" role="alert">
                {serverError}
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientAppShell>
  );
};

export default CapAsses;
